<template>
	<v-defaults-provider :defaults="overlayDefaults">
		<v-card id="log-list" class="fill-height" ref="rootCard">
			<v-card id="station-log-toolbar-container" ref="toolbarContainer" rounded="b-0">
				<Toolbar :items="toolbarItems" :panelParams="params"/>
			</v-card>
			<v-card id="station-log-container" class="d-flex fill-height" rounded="t-0">
				<perfect-scrollbar v-if="selectedStation" id="station-log-clock" ref="logClockScrollbar"
					minScrollbarLength="4" maxScrollbarLength="4">
					<Clock v-if="selectedStation"
						:selectedStation="selectedStation"
						:selectedDateTime="datetime"
						:stationCurrentHour="stationCurrentHour"
						:from="24" :to="24"
						:scrollbar="true"
						:tabHeight="clockTabHeight"
						@selectDateTime="getPlaylistAssetsBySchedule"/>
				</perfect-scrollbar>
				<perfect-scrollbar v-if="selectedStation" :options="scrollbarOptions" :watch-options="true"
					ref="logEventsContainer">
					<v-expansion-panels multiple variant="accordion" class="flex-column"
						:class="{'fill-height': isEmptyPlaylist}">
						<draggable :list="playlist?.Events"
							:group="{ name: 'log', pull: 'clone', put: ['library-assets', 'hotkeys']}"
							filter=".not-draggable"
							:force-fallback="true"
							:fallback-on-body="true"
							drag-class="dragged-log"
							class="drop-container"
							data-drop-container="log"
							:move="onMove"
							@end="onDropItem"
							:clone="handleDraggedClone">
							<StationLogItem v-for="event in playlist?.Events" :key="event.EventID"
								:selectedStation="selectedStation"
								:event="event"
								:id="event.EventID"
								:data-station-id="event.StationID"
								:data-playlist-id="event.PlaylistID"
								:data-child-playlist-id="event.ChildPlaylistID"
								:class="{'not-draggable': !isDraggableEvent(event)}"
								:dropTargetEvent="loadDropTargetEvent(event.EventID)"
								:cuttingEvent="cuttingEvent"
								:copiedEvent="copiedEvent"
								:parentComponent="this"
								@cut-event="cutEvent(event)"
								@copy-event="copyEvent(event)"
								@paste-event="pasteEvent"
								@menu-open="(open) => childMenuOpen(open, event)"/>
							<PlaylistEmptyContainer v-if="!!selectedStation && isEmptyPlaylist"
								:id="`empty-playlist${playlist?.PlaylistID ? `-${playlist.PlaylistID}` : ''}`"
								class="not-draggable empty-playlist-event"
								:playlist="playlist"
								:data-playlist-id="playlist?.PlaylistID"
								:dropTargetEvent="loadDropTargetEvent(`empty-playlist${playlist?.PlaylistID ? `-${playlist.PlaylistID}` : ''}`)"
								:create-playlist-btn-enabled="!!playlist?.isEmptyPlaceholder"
								@create-playlist="createPlaylist"/>
						</draggable>
					</v-expansion-panels>
				</perfect-scrollbar>
				<NoStationSelectedContainer v-if="!selectedStation"/>
				<Loader v-else :value="!playlist || loading"/>
			</v-card>

			<v-dialog v-model="overrideSegueTransitionsModal.open" width="350" persistent>
				<GeneralConfirmation message="Do you want to Override Segue Transitions?"
					:button-options="buttonOptions"
					@agree="() => overrideSegueTransitionsModal.resolve(true)"
					@cancel="() => overrideSegueTransitionsModal.resolve(false)"
				/>
			</v-dialog>
		</v-card>
	</v-defaults-provider>
</template>

<script>
/* Libraries */
import * as _ from "lodash";
import {VueDraggableNext} from "vue-draggable-next";
import emitter from "@/emitter";
import moment from "moment-timezone";
import {uuid} from "vue-uuid";

/* Modules */
import {VBtn, VTooltip} from "vuetify/components";
import Clock from "@/components/Clock.vue";
import Loader from "@/components/Loader.vue";
import NoStationSelectedContainer from "@/components/NoStationSelectedContainer.vue";
import StationLogItem from "@/components/StationLogItem.vue";
import Toolbar from "@/components/Toolbar.vue";
import GeneralConfirmation from "@/components/dialogs/GeneralConfirmation.vue";
import PlaylistEmptyContainer from "@/components/PlaylistEmptyContainer.vue";
import PlaylistDateTimePicker from "@/components/PlaylistDateTimePicker.vue";

/* Enums */
import EventTypes from "@/enums/EventTypes";
import PlaylistTypes from "@/enums/PlaylistTypes";
import EventStatus from "@/enums/EventStatus";

/* Mixins */
import CutCopyPasteMixin from "@/mixins/CutCopyPaste";
import DragAndDropMixin from "@/mixins/DragAndDrop"
import PanelPropertiesMixin from "@/mixins/PanelProperties";

// list of handlers to scroll the element
const defaultScrollbarHandlers = ["click-rail", "drag-thumb", "keyboard", "wheel", "touch"];

export default {
	name: "StationLog",

	components: {
		Clock,
		GeneralConfirmation,
		Loader,
		NoStationSelectedContainer,
		PlaylistEmptyContainer,
		StationLogItem,
		Toolbar,
		draggable: VueDraggableNext
	},

	mixins: [CutCopyPasteMixin, DragAndDropMixin, PanelPropertiesMixin],

	props: ["params"],

	data() {
		return {
			loading: false,
			selectedDateTime: moment.tz(this.stationTimezone).startOf("hour"),
			selectedYear: null,
			selectedMonth: null,
			draggedEvent: null,
			dropTargetEvent: null,
			componentName: "log",
			waitForSequencerSnapshot: false,
			clockTabHeight: null,
			buttonOptions: {
				agreeBtn: {
					enabled: true,
					message: "Yes"
				},
				cancelBtn: {
					enabled: true,
					message: "No"
				}
			},
			scrollbarOptions: {
				handlers: defaultScrollbarHandlers
			},
			datePickerOpen: false,
			timePickerOpen: false
		}
	},

	computed: {
		toolbarItems() {
			if (!this.selectedStation) {
				return [];
			}

			return [
				{
					component: PlaylistDateTimePicker,
					props: {
						selectedStationID: this.selectedStationID,
						datePickerOpen: this.datePickerOpen,
						timePickerOpen: this.timePickerOpen,
						selectedDateTime: this.selectedDateTime,
						timezone: this.stationTimezone,
						loading: this.loading
					},
					actions: {
						datePickerClicked: (open) => {
							this.datePickerOpen = open;
							this.timePickerOpen = false;
						},
						onUpdateMonth: (month) => {
							this.selectedMonth = month;

							this.loadPlaylistsDatePicker({year: this.selectedYear, month: this.selectedMonth});
						},
						onUpdateYear: (year) => {
							this.selectedYear = year;

							this.loadPlaylistsDatePicker({year: this.selectedYear, month: this.selectedMonth});
						},
						onDayClicked: (date) => {
							this.datePickerOpen = false;

							const time = this.selectedDateTime.format("HH:mm");
							const datetime = moment.tz(
								`${date.format("YYYY-MM-DD")} ${time}`,
								this.stationTimezone
							);
							if (this.selectedDateTime.isSame(datetime)) {
								return;
							}
							this.getPlaylistAssetsBySchedule(datetime);
						},
						timePickerClicked: (open) => {
							this.timePickerOpen = open;
							this.datePickerOpen = false;
						},
						onTimeClicked: (time) => {
							this.timePickerOpen = false;

							const date = this.selectedDateTime.format("YYYY-MM-DD");
							const datetime = moment.tz(`${date} ${time}`, this.stationTimezone);
							if (this.selectedDateTime.isSame(datetime)) {
								return;
							}
							this.getPlaylistAssetsBySchedule(datetime);
						}
					}
				},
				{
					component: VTooltip,
					props: {
						text: "Home",
						location: "bottom",
						openDelay: 1000
					},
					slots: [
						{
							name: "activator",
							items: (data) => [
								{
									component: VBtn,
									tooltip: {
										text: "Home",
										openDelay: 1000
									},
									props: {
										...data.props,
										icon: "mdi-home-circle",
										variant: "text",
										class: "ml-3",
										size: 40,
										elevation: 1,
										disabled: this.loading,
										style: {"border-radius": "3px"}
									},
									actions: {
										click: this.scrollToNowPlaying
									}
								}
							]
						}
					]
				}
			];
		},

		datetime() {
			return this.selectedDateTime || moment.tz(this.stationTimezone).startOf("hour");
		},

		loadedPlaylists() {
			return this.$store.getters.loadedSchedulePlaylistsByStationID(this.selectedStationID);
		},

		subscriptions() {
			return this.$store.getters.stationLogViewSubscriptions(this.selectedStationID, this.$.uid)
			           .filter(p => p.PlaylistType === PlaylistTypes.Schedule);
		},

		emptyPlaylists() {
			return this.$store.getters.getEmptyPlaylists();
		},

		playlist() {
			let playlist = this.loadedPlaylists.find(p => this.datetime.isSame(p.ScheduledStartDateTime));
			if (playlist) {
				playlist = {...playlist}; // Avoid mutating the store object in place
				playlist.Events = (playlist.Events || [])
					.filter(
						e => [
							EventTypes.PlayAsset,
							EventTypes.SpotBlock,
							EventTypes.LocalBreak,
							EventTypes.Comment,
							EventTypes.DVTBlock,
							EventTypes.Rejoin,
							EventTypes.MusicFillPos
						].includes(e.EventType)
					);
			}

			return !_.isEmpty(playlist?.Events) ? playlist :
			       this.emptyPlaylists.find(p => this.datetime.isSame(p.ScheduledStartDateTime));
		},

		playlistsEvents() {
			// Load the events of the Playlist's module subscribed playlists
			const playlists = this.loadedPlaylists;
			if (!playlists.length) {
				return [];
			}
			// Returns a flattened array of all events from the loaded playlists
			return playlists.flatMap(p => p.Events);
		},

		currentEvent() {
			return _.findLast(
					   this.$store.getters.getSequencerItems(this.selectedStationID),
					   e => e.StatusCode === EventStatus.Current
				   ) ??
			       _.findLast(this.playlistsEvents, e => e.StatusCode === EventStatus.Current);
		},

		isEmptyPlaylist() {
			return this.playlist?.isEmptyPlaceholder || _.isEmpty(this?.playlist?.Events);
		}
	},

	methods: {
		onMove(evt, originalEvent) {
			const draggedElement = document.querySelector(".dragged-log");
			this.onDragItem(evt, originalEvent, draggedElement);
			return false;
		},

		async getPlaylistAssetsById(playlistID, {stationChanged = false, scrollToNowPlaying = false} = {}) {
			if (!this.loading) {
				this.loading = true;

				let playlist = this.$store.getters.getPlaylistById(
					{stationID: this.selectedStationID, playlistID: playlistID}
				);
				if (!playlist) {
					playlist = await this.$store.dispatch("getPlaylistWithAssetsById", {playlistID});
				}
				if (stationChanged) {
					this.selectedDateTime.tz(this.stationTimezone, true);
				}
				else {
					this.selectedDateTime = moment.tz(
						playlist.ScheduledStartDateTime,
						this.stationTimezone
					);
				}
				await this.subscribePlaylists([playlist]);
				this.loading = false;

				if (scrollToNowPlaying) {
					await this.$nextTick();
					this.scrollToNowPlaying();
				}
			}
		},

		async getPlaylistAssetsBySchedule(dateTime, force = false) {
			if (!force && this.selectedDateTime.isSame(dateTime)) {
				return;
			}
			if (!this.loading) {
				this.loading = true;
				if (force) {
					this.selectedDateTime.tz(this.stationTimezone, true);
				}
				else {
					this.selectedDateTime = moment.tz(dateTime, this.stationTimezone);
				}

				const playlist = this.loadedPlaylists?.find(p => this.selectedDateTime.isSame(p.ScheduledStartDateTime));

				if (playlist) {
					await this.unsubscribePlaylists(this.subscriptions);
					await this.subscribePlaylists([playlist]);
					this.loading = false;
					return;
				}

				await this.unsubscribePlaylists(this.subscriptions);

				const from = this.selectedDateTime.clone().startOf("hour");
				const to = this.selectedDateTime.clone().endOf("hour");

				try {
					const playlists = await this.$store.dispatch(
						"getPlaylistWithAssetsBySchedule",
						{stationID: this.selectedStationID, from, to, tz: this.stationTimezone, recalcLoadedPlaylists: false}
					);

					for (let dt = from.clone(); dt.isSameOrBefore(to); dt.add(1, "hour")) {
						let playlist = playlists.find(pl => pl.ScheduledStartDateTime === dt.toISOString())
						if (!playlist) {
							const PlaylistID = `empty-playlist-${uuid.v1()}`;
							playlist = {
								PlaylistID,
								PlaylistType: PlaylistTypes.Schedule,
								ScheduledStartDateTime: dt.tz(this.stationTimezone).toISOString(),
								Events: [],
								TopOfTheHour: {
									PlaylistID,
									AirStartDateTime: dt.tz(this.stationTimezone).toISOString(),
									EventID: `top-of-the-hour-${PlaylistID}`
								},
								isEmptyPlaceholder: true
							}
							this.$store.commit("addEmptyPlaylist", playlist);
						}
						else if (_.isEmpty(playlist?.Events)) {
							this.$store.commit("addEmptyPlaylist", playlist);
						}
					}
					await this.subscribePlaylists(playlists);
				} finally {
					this.loading = false;
				}
			}
		},

		loadPlaylistsDatePicker({year, month}) {
			const promises = [];
			const stationID = this.selectedStationID;
			const start = moment({year, month}).tz(this.stationTimezone).startOf("week");
			const end = moment({year, month}).endOf("month").tz(this.stationTimezone).endOf("week");

			while (start.isSameOrBefore(end)) {
				let from = start.clone();
				let to = start.clone().endOf("week");
				start.add(1, "week");

				const dateRange = `${from.format("YYYY-MM-DD")}_${to.format("YYYY-MM-DD")}`;

				//If a request for this date range has already been initiated, continue with the next week.
				if (this.$store.getters.playlistsNoAssetsLoadedByDateRange(stationID, dateRange)) {
					continue;
				}

				//Set the key in the store as an identifier that a request for this date range has already been initiated.
				this.$store.commit("addLoadedPlaylistNoAssets", {stationID, dateRange});

				promises.push(
					this.$store.dispatch(
							"getPlaylistBySchedule",
							{stationID, from, to, params: {ReturnChildEvents: false}}
						)
					    .then(response => {
							return {
								stationID,
								dateRange,
								playlists: response.data
							};
						})
					    .catch(() => {
							return {
								stationID,
								dateRange,
								playlists: null
							};
						})
				);
			}

			if (promises.length > 0) {
				Promise.all(promises).then(results => {
					results.forEach(result => this.$store.commit("addLoadedPlaylistNoAssets", result));
				});
			}
		},

		async subscribePlaylists(playlists) {
			// Playlists
			await this.$store.dispatch("addRemoveStationLogSubscriptions", {playlists, componentID: this.$.uid});

			// Spotblocks and local breaks playlists
			for (const playlist of playlists) {
				const spotBlocks = (playlist?.Events || []).filter(({EventType}) => EventType === EventTypes.SpotBlock);
				const spotBlockPlaylists = _.compact(_.map(spotBlocks, "ChildPlaylist"));
				if (spotBlockPlaylists.length) {
					await this.$store.dispatch("addRemoveStationLogSubscriptions", {playlists: spotBlockPlaylists, componentID: this.$.uid});
				}

				const localBreaks =
					(playlist?.Events || []).filter(({EventType}) => EventType === EventTypes.LocalBreak);
				if (localBreaks.length) {	//call "subscribePlaylists" giving the local break playlists to subscribe to the inner spot block playlists too
					const localBreakPlaylists = _.compact(_.map(localBreaks, "ChildPlaylist"));
					if (localBreakPlaylists.length) {
						await this.subscribePlaylists(localBreakPlaylists);
					}
				}
			}
		},

		async unsubscribePlaylists(playlists) {
			await this.$store.dispatch("addRemoveStationLogSubscriptions", {playlists, componentID: this.$.uid, subscribe: false});

			// Spotblocks and local breaks playlists
			for (const playlist of playlists) {
				const spotBlocks = (playlist?.Events || []).filter(({EventType}) => EventType === EventTypes.SpotBlock);
				if (spotBlocks.length) {
					const spotBlockPlaylists = _.compact(_.map(spotBlocks, "ChildPlaylist"));
 				if (spotBlockPlaylists.length) {
 					await this.$store.dispatch(
 						"addRemoveStationLogSubscriptions",
 						{playlists: spotBlockPlaylists, componentID: this.$.uid, subscribe: false}
 					);
 				}
				}

				const localBreaks = (playlist?.Events || [])
					.filter(({EventType}) => EventType === EventTypes.LocalBreak);
				if (localBreaks.length) {	//call "unsubscribePlaylists" giving the local break playlists to subscribe to the inner spot block playlists too
					const localBreakPlaylists = _.compact(_.map(localBreaks, "ChildPlaylist"));
					if (localBreakPlaylists.length) {
						await this.unsubscribePlaylists(localBreakPlaylists);
					}
				}
			}
		},

		scrollToNowPlaying() {
			const scrollToEvent = this.currentEvent;

			if (scrollToEvent) {
				const container = this.$refs.logEventsContainer?.$el;
				const scrollToEventEl = container?.querySelector(`[id="${scrollToEvent.EventID}"]`);

				if (!scrollToEventEl) {
					this.getPlaylistAssetsById(scrollToEvent.PlaylistID, {scrollToNowPlaying: true});
					return;
				}

				container.scrollTo({
					top: scrollToEventEl.offsetTop,
					left: 0
				});
			}
		},

		childMenuOpen(open, event) {
			if (this.copiedEvent) {
				this.copiedEvent.pasteBeforeEvent = open ? event.EventID : null;
			}
			if (this.cuttingEvent) {
				this.cuttingEvent.pasteBeforeEvent = open ? event.EventID : null;
			}

			// If a child's context menu is open, disable the log list scrolling by setting the scrollbar's handlers array to []
			this.scrollbarOptions.handlers = open ? [] : defaultScrollbarHandlers;
		},

		setClockTabHeight() {
			const el = this.$refs.rootCard?.$el;
			const containerHeight = el.clientHeight - this.$refs.toolbarContainer.$el.offsetHeight;
			this.clockTabHeight = containerHeight > 500 ?
			                      (containerHeight / 24) : (containerHeight / 12);
		},

		//todo check method
		createPlaylist() {
			this.$store.dispatch("createPlaylist", {
					PlaylistID: uuid.v1(),
					OrganizationID: this.$store.getters.getOrgID(),
					Title: moment(this.datetime).format("MM/DD/YYYY hh:mm:ss A [Hour]: HH"),
					StationID: this.selectedStationID,
					Priority: 110,
					Origin: "LC",
					ScheduledStartDateTime: this.datetime.toISOString(),
					PlaylistType: PlaylistTypes.Schedule
				})
			    .then(async playlist => {
					await this.$store.dispatch("addRemoveStationLogSubscriptions", {playlists: [playlist], componentID: this.$.uid});
					this.$store.commit(
						"removeEmptyPlaylist",
						this.emptyPlaylists.find(p => this.datetime.isSame(p.ScheduledStartDateTime)).PlaylistID
					);
					this.$store.commit("addEmptyPlaylist", playlist);
				})
		}
	},

	watch: {
		selectedDateTime: {
			handler(dateTime) {
				if (dateTime) {
					this.selectedYear = dateTime.year();
					this.selectedMonth = dateTime.month();
					this.loadPlaylistsDatePicker({year: this.selectedYear, month: this.selectedMonth});
				}
			},
			immediate: true
		},

		selectedStation: {
			handler(station, previousStation) {
				if (station) {
					if (this.subscriptions.length > 0) {
						this.unsubscribePlaylists(this.subscriptions);
					}
					if (this.currentEvent) {
						const stationChanged = !!previousStation && station.StationID !== previousStation.StationID;
						this.getPlaylistAssetsById(
							this.currentEvent.PlaylistID,
							{
								stationChanged: stationChanged,
								scrollToNowPlaying: !stationChanged
							}
						);
					}
					else {
						const currentHour = moment.tz(this.stationTimezone).startOf("hour");
						this.getPlaylistAssetsBySchedule(currentHour, true);
						this.waitForSequencerSnapshot = true;
					}
					this.loading = false;

					if (station.StationID !== previousStation?.StationID) {
						this.$store.commit("incrementStationPlaylistModuleCounter", station.StationID);
					}
					if (previousStation) {
						this.$store.commit("clearStationPlaylists", previousStation.StationID);
					}
				}
			},
			immediate: true
		},

		currentEvent(currentEvent) {
			if (currentEvent && this.waitForSequencerSnapshot) {
				this.getPlaylistAssetsById(this.currentEvent.PlaylistID);
				this.waitForSequencerSnapshot = false;
			}
		}
	},

	created() {
		this.$store.commit("setContainerByRef", {ref: "StationLogComponent", component: this});

		// Handle escape button click while dragging or if cut/copied an event - cancel drag/cut/copy
		document.addEventListener("keydown", this.cancelDraggingOrCutOrCopyOnEscape);
	},

	mounted() {
		this.$nextTick(this.setClockTabHeight);

		emitter.on("docking:layoutChanged", this.setClockTabHeight);

		window.addEventListener("resize", this.setClockTabHeight);
	},

	beforeUnmount() {
		this.$store.commit("unsetContainerByRef", "StationLogComponent");

		emitter.off("docking:layoutChanged", this.setClockTabHeight);

		document.removeEventListener("keydown", this.cancelDraggingOrCutOrCopyOnEscape);
		window.removeEventListener("resize", this.setClockTabHeight);
	}
}
</script>

<style lang="scss" scoped>
.sortable-ghost {
	background-color: rgba($ihm-blue, .07) !important;
}
</style>
